/**
 * UI adapter: bridges React tick scheduler to RunGameTickUseCase (UC-01)
 * Enable via architectureFeatures.useCleanArchitectureTick
 */
import { useCallback } from 'react';
import type {
  Grid,
  CityStats,
  ResearchState,
  StatDeltas,
  SimulationBreakdown,
} from '../../../../types';
import type { OrgImpactMetrics } from '../../../../utils/orgImpactCalculator';
import { runGameTickUseCase } from '../../../infrastructure/di/container';
import { architectureFeatures } from '../../../infrastructure/config/features';
import { simulationEngine } from '../../../domain/services/SimulationEngine';

export interface GameTickState {
  grid: Grid;
  stats: CityStats;
  research: ResearchState;
  tickNumber: number;
}

export interface GameTickResult extends GameTickState {
  netBudget: number;
  netComp: number;
  dailyThreat: number;
  deltas: StatDeltas;
  buildingCounts: Record<string, number>;
  revenueHalted: boolean;
  violations: string[];
  violationTiles: { x: number; y: number }[];
  breakdown: SimulationBreakdown;
  orgImpact?: OrgImpactMetrics;
}

/** Run one tick through clean architecture use case */
export function executeGameTick(state: GameTickState): GameTickResult {
  if (!architectureFeatures.useCleanArchitectureTick) {
    throw new Error('useCleanArchitectureTick feature flag is disabled');
  }

  const result = runGameTickUseCase.execute(state);
  return {
    grid: result.grid,
    stats: result.stats,
    research: result.research,
    tickNumber: state.tickNumber,
    netBudget: result.netBudget,
    netComp: result.netComp,
    dailyThreat: result.dailyThreat,
    deltas: result.deltas,
    buildingCounts: result.buildingCounts,
    revenueHalted: result.revenueHalted,
    violations: result.violations,
    violationTiles: result.violationTiles,
    breakdown: result.breakdown,
    orgImpact: result.orgImpact,
  };
}

/**
 * Hook helper for future App.tsx integration.
 * Returns a stable callback that runs UC-01 when feature flag is on.
 */
export function useGameTickRunner() {
  return useCallback((state: GameTickState): GameTickResult | null => {
    if (!architectureFeatures.useCleanArchitectureTick) {
      return null;
    }
    return executeGameTick(state);
  }, []);
}

/** Legacy fallback metrics — same engine, no use-case wrapper (migration helper) */
export function executeLegacyTickMetrics(grid: Grid, stats: CityStats, tickNumber: number) {
  const connectedGrid = simulationEngine.calculateConnectivity(grid);
  return simulationEngine.calculateTickMetrics(connectedGrid, stats, tickNumber);
}

/** Resolve one tick — UC-01 when flag on, else legacy engine path */
export function resolveTickMetrics(
  state: GameTickState
): Omit<GameTickResult, 'stats' | 'research'> & Pick<GameTickState, 'stats' | 'research'> {
  if (architectureFeatures.useCleanArchitectureTick) {
    return executeGameTick(state);
  }
  const connectedGrid = simulationEngine.calculateConnectivity(state.grid);
  const metrics = simulationEngine.calculateTickMetrics(connectedGrid, state.stats, state.tickNumber);
  return {
    grid: connectedGrid,
    stats: state.stats,
    research: state.research,
    tickNumber: state.tickNumber,
    ...metrics,
  };
}